declare const grecaptcha: {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
};

interface ErrorResponse {
    message?: string;
    errors?: Record<string, string[]>;
}

/**
 * A company's enquiry form: submits as JSON and shows the thanks or the errors in place.
 */
export class EnquiryForm {
    private readonly button: HTMLButtonElement;
    private readonly errors: HTMLUListElement;

    constructor(private readonly form: HTMLFormElement) {
        this.button = form.querySelector(
            'button[type=submit]'
        ) as HTMLButtonElement;
        this.errors = form.querySelector(
            '[data-enquiry-errors]'
        ) as HTMLUListElement;

        form.addEventListener('submit', (event) => {
            event.preventDefault();
            void this.submit();
        });
    }

    private async submit(): Promise<void> {
        this.button.disabled = true;
        try {
            const body = new FormData(this.form);
            // Switched off, the form carries its own placeholder token.
            if (this.form.dataset.recaptcha !== 'off') {
                body.set('g-recaptcha-response', await this.captchaToken());
            }
            const response = await fetch(this.form.action, {
                method: 'POST',
                body,
                headers: { Accept: 'application/json' },
            });

            if (response.ok) {
                const sent = document.querySelector(
                    '[data-enquiry-sent]'
                ) as HTMLElement;
                this.form.hidden = true;
                sent.hidden = false;
                sent.scrollIntoView({ block: 'nearest' });
                return;
            }

            const result = (await response
                .json()
                .catch(() => ({}))) as ErrorResponse;
            this.showErrors(
                result.errors !== undefined
                    ? Object.values(result.errors).flat()
                    : [
                          result.message ??
                              'Your enquiry could not be sent. Please try again.',
                      ]
            );
        } catch (error) {
            console.error(error);
            this.showErrors([
                'Your enquiry could not be sent. Please try again.',
            ]);
        } finally {
            this.button.disabled = false;
        }
    }

    private captchaToken(): Promise<string> {
        const siteKey = this.form.dataset.siteKey ?? '';
        if (siteKey === '') {
            throw new Error('The enquiry form has no reCAPTCHA site key.');
        }

        return new Promise((resolve, reject) => {
            grecaptcha.ready(() => {
                grecaptcha
                    .execute(siteKey, { action: 'submit' })
                    .then(resolve, reject);
            });
        });
    }

    private showErrors(messages: string[]): void {
        this.errors.replaceChildren(
            ...messages.map((message) => {
                const item = document.createElement('li');
                item.textContent = message;
                return item;
            })
        );
        this.errors.hidden = false;
        this.errors.scrollIntoView({ block: 'nearest' });
    }
}
